// src/pages/EditCollection.jsx
import React, { useState, useEffect } from "react";
import MasterLayout from "../Sections/MasterLayout";
import { X, Plus, Image as ImageIcon, Edit3 } from "lucide-react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import {
  add_collection,
  server_post_data,
  get_all_collections,
  server_post_json,
  product_by_category,
} from "../../ServiceConnection/serviceconnection";
import { retrieveData } from "../../LocalConnection/LocalConnection";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Loader from "./Loader";

export default function EditCollection() {
  const navigate = useNavigate();
  const { id: collection_id } = useParams();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [slug, setSlug] = useState("");
  const [status, setStatus] = useState("Active");
  const [image, setImage] = useState(null); // new uploaded file
  const [existingImage, setExistingImage] = useState(null); // from server
  const [collectionProducts, setCollectionProducts] = useState([]);

  // SEO
  const [seoOpen, setSeoOpen] = useState(false);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");

  // Loaders
  const [pageLoading, setPageLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Conditions
  const [matchType, setMatchType] = useState("any");
  const [conditions, setConditions] = useState([
    { field: "tag", operator: "equals", value: "" },
  ]);

  // Error states for mandatory fields
  const [titleError, setTitleError] = useState(false);
  const [slugError, setSlugError] = useState(false);
  const [conditionErrors, setConditionErrors] = useState([]);
  // Collection Type
  const [type, setType] = useState("category");
  /* AUTO SEO */
  useEffect(() => {
    if (!title) {
      setSlug("");
      return;
    }

    const generatedSlug = title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-");

    setSlug(generatedSlug);
    setMetaTitle(title.slice(0, 70));
  }, [title]);

  useEffect(() => {
    if (!description) return;
    const text = description.replace(/<[^>]+>/g, "");
    setMetaDescription(text.slice(0, 160));
  }, [description]);

  /* CONDITIONS */
  const addCondition = () => {
    setConditions([
      ...conditions,
      { field: "tag", operator: "equals", value: "" },
    ]);
    setConditionErrors([...conditionErrors, false]); // no error initially
  };
  const updateCondition = (index, key, value) => {
    const copy = [...conditions];
    copy[index][key] = value;
    setConditions(copy);

    // Clear error for this row when user types
    const errCopy = [...conditionErrors];
    errCopy[index] = false;
    setConditionErrors(errCopy);
  };

  const removeCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
    setConditionErrors(conditionErrors.filter((_, i) => i !== index));
  };

  /* IMAGE */
  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Optional: restrict file type/size
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file");
      return;
    }

    setImage(file);
  };

  // Fetch existing collection data
  const master_get_data = async () => {
    try {
      setPageLoading(true);

      const response = await server_post_json(get_all_collections, {
        collection_id,
      });

      const data = response?.data?.data;
      if (!data) return;

      setTitle(data.title || "");
      setSlug(data.slug || "");
      setDescription(data.description || "");
      setStatus(data.status || "Active");
      setMatchType(data.matchType || "any");
      setConditions(data.conditions || []);
      setType(data.type || "category");
      setMetaTitle(data.seo?.metaTitle || "");
      setMetaDescription(data.seo?.metaDescription || "");
      setExistingImage(data.imageFull || data.image || null);

      // ✅🔥 YAHI MAIN FIX
      if (data.slug) {
        fetchProductsByCollection(data.slug);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load collection data");
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (collection_id) {
      master_get_data();
    }
  }, [collection_id]);

  const fetchProductsByCollection = async (collectionSlug) => {
    if (!collectionSlug) return;

    try {
      const res = await server_post_json(product_by_category, {
        category: collectionSlug,
      });

      const data = res.data.products;
      setCollectionProducts(data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load collection products");
    }
  };

  console.log("collectionProducts", collectionProducts);
  // Validation
  const validateForm = () => {
    let valid = true;

    // Title
    if (!title.trim()) {
      setTitleError(true);
      valid = false;
    } else {
      setTitleError(false);
    }

    // Slug
    if (!slug.trim()) {
      setSlugError(true);
      valid = false;
    } else {
      setSlugError(false);
    }

    // Conditions value validation
    const newErrors = conditions.map((c) => !c.value.trim());
    setConditionErrors(newErrors);

    if (newErrors.some((err) => err)) {
      valid = false;
      toast.error("Please fill all condition values");
    }

    return valid;
  };

  const saveCollection = async () => {
    if (!validateForm()) return;

    try {
      setSubmitLoading(true);

      const fd = new FormData();

      if (collection_id) {
        fd.append("collection_id", collection_id);
      }

      fd.append("title", title.trim());
      fd.append("slug", slug.trim());
      fd.append("description", description);
      fd.append("status", status);
      fd.append("matchType", matchType);
      fd.append("conditions", JSON.stringify(conditions));
      fd.append("type", type);

      fd.append("seo", JSON.stringify({ metaTitle, metaDescription }));

      if (image) {
        fd.append("image", image);
      }

      const res = await server_post_data(add_collection, fd);

      if (res?.data?.success) {
        toast.success(
          collection_id
            ? "Collection updated successfully"
            : "Collection added successfully"
        );
        setTimeout(() => navigate("/collection"), 800);
      } else {
        toast.error(res?.data?.message || "Failed to save collection");
      }
    } catch (err) {
      console.error(err);
      toast.error("Server error");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <MasterLayout>
      {pageLoading && <Loader />}
      {submitLoading && <Loader />}

      <div className="bg-gray-50">
        <div className="px-4 py-6">
          {/* Header */}
          <div className="ml-0 md:ml-2 mb-4">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-800">
              {collection_id ? "Edit Collection" : "Add Collection"}
            </h2>
          </div>

          <form className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* LEFT */}
            <div className="lg:col-span-2 space-y-4">
              {/* TITLE */}
              <div className="bg-white p-5 rounded-xl border">
                <label className="text-sm font-medium">
                  Title <span className="text-red-600">*</span>
                </label>
                <input
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setTitleError(false);
                  }}
                  className={`w-full mt-2 px-3 py-2 border rounded-lg ${
                    titleError ? "border-red-500" : ""
                  }`}
                  placeholder="e.g. Winter Collection 2025"
                />
                {titleError && (
                  <span className="text-xs text-red-600 mt-1 block">
                    Title is required
                  </span>
                )}

                <label className="text-sm font-medium mt-4 block">
                  Description
                </label>
                <div className="mt-4 my-editor">
                  <CKEditor
                    editor={ClassicEditor}
                    data={description}
                    onChange={(event, editor) => {
                      setDescription(editor.getData());
                    }}
                  />
                </div>
              </div>

              {/* TYPE */}
              <div className="bg-white p-5 rounded-xl border">
                <label className="text-sm font-medium">Collection Type</label>

                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full mt-2 px-3 py-2 border rounded-lg"
                >
                  <option value="category">
                    Category (Visible on website)
                  </option>
                  <option value="collection">Collection (Logic only)</option>
                </select>

                <p className="text-xs text-gray-500 mt-2">
                  Categories appear on the website. Collections are used for
                  logic like New Arrivals, Best Sellers, etc.
                </p>
              </div>

              {/* CONDITIONS */}
              <div className="bg-white p-5 rounded-xl border">
                <h3 className="text-sm font-medium mb-4">Conditions</h3>

                <div className="flex gap-6 text-sm mb-4">
                  <label className="flex gap-2">
                    <input
                      type="radio"
                      checked={matchType === "all"}
                      onChange={() => setMatchType("all")}
                    />
                    All conditions
                  </label>
                  <label className="flex gap-2">
                    <input
                      type="radio"
                      checked={matchType === "any"}
                      onChange={() => setMatchType("any")}
                    />
                    Any condition
                  </label>
                </div>

                {conditions.map((c, i) => (
                  <div key={i} className="flex flex-col gap-1 mb-4">
                    <div className="flex gap-2 items-center">
                      <select className="border border-gray-300 px-2 py-1 rounded">
                        <option>Tag</option>
                      </select>

                      <select className="border border-gray-300 px-2 py-1 rounded">
                        <option>is equal to</option>
                      </select>

                      <input
                        value={c.value}
                        onChange={(e) =>
                          updateCondition(i, "value", e.target.value)
                        }
                        className={`border flex-1 px-2 py-1 rounded ${
                          conditionErrors[i]
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="e.g. winter"
                      />

                      <button
                        type="button"
                        onClick={() => removeCondition(i)}
                        className="text-gray-500 hover:text-red-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {conditionErrors[i] && (
                      <span className="text-xs text-red-600">
                        Please fill this value
                      </span>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addCondition}
                  className="text-sm text-blue-600 hover:underline"
                >
                  + Add another condition
                </button>
              </div>

              {/* PRODUCTS PREVIEW */}
              <div className="bg-white p-5 rounded-xl border">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-medium">Products</h3>
                </div>

                {collectionProducts.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No products in this collection
                  </p>
                ) : (
                  <div className="divide-y">
                    {collectionProducts.map((p, index) => (
                      <div
                        key={p.product_id}
                        className="flex items-center gap-3 py-3"
                      >
                        <span className="text-xs text-gray-400 w-4">
                          {index + 1}.
                        </span>

                        <img
                          src={p.mainImages?.[0] || p.image}
                          className="w-12 h-16 object-cover rounded border"
                          alt={p.title}
                        />

                        <div className="flex-1">
                          <p className="text-sm font-medium">{p.title}</p>
                          <p className="text-xs text-gray-500">
                            Inventory: {p.totalInventory || 0}
                          </p>
                        </div>

                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            p.status === "Active"
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {p.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* SEO PREVIEW */}
              <div className="bg-white p-5 rounded-xl border">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-medium">
                    Search engine listing preview
                  </h3>
                  <button
                    type="button"
                    onClick={() => setSeoOpen(!seoOpen)}
                    className="text-blue-600 text-sm flex items-center gap-1"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit
                  </button>
                </div>

                <div className="bg-gray-50 border rounded-lg p-4 text-xs">
                  <p className="text-gray-600">LazyX</p>
                  <p className="text-blue-600">
                    https://lazyx.in/collections/{slug || "your-collection"}
                  </p>
                  <p className="text-blue-700 font-medium text-sm mt-1">
                    {metaTitle || title || "Your Collection Title"}
                  </p>
                  <p className="text-gray-700">
                    {metaDescription || "Collection description"}
                  </p>
                </div>

                {seoOpen && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="text-xs font-medium">Meta title</label>
                      <input
                        value={metaTitle}
                        onChange={(e) => setMetaTitle(e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded"
                      />
                      <p className="text-xs text-gray-500">
                        {metaTitle.length}/70 characters
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-medium">
                        Meta description
                      </label>
                      <textarea
                        rows={3}
                        value={metaDescription}
                        onChange={(e) => setMetaDescription(e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded"
                      />
                      <p className="text-xs text-gray-500">
                        {metaDescription.length}/160 characters
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-medium">
                        URL handle <span className="text-red-600">*</span>
                      </label>
                      <div className="flex mt-1">
                        <span className="inline-flex items-center px-3 text-xs text-gray-500 bg-gray-100 border border-r-0 rounded-l-lg">
                          https://lazyx.in/collections/
                        </span>
                        <input
                          value={slug}
                          onChange={(e) => {
                            const cleaned = e.target.value
                              .toLowerCase()
                              .replace(/[^a-z0-9-]/g, "")
                              .replace(/\s+/g, "-");
                            setSlug(cleaned);
                            setSlugError(false);
                          }}
                          className={`flex-1 px-3 py-2 border rounded-r-lg text-sm ${
                            slugError ? "border-red-500" : ""
                          }`}
                          placeholder="winter-collection-25"
                        />
                      </div>
                      {slugError && (
                        <span className="text-xs text-red-600 mt-1 block">
                          Slug is required
                        </span>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        This will be the collection URL
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT */}
            <div className="space-y-6 lg:sticky lg:top-6 self-start">
              {/* STATUS */}
              <div className="bg-white p-5 rounded-xl border">
                <label className="text-sm font-medium">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full mt-2 px-3 py-2 border rounded-lg"
                >
                  <option>Active</option>
                  <option>Draft</option>
                </select>
              </div>

              {/* IMAGE */}
              <div className="bg-white p-5 rounded-xl border">
                <label className="text-sm font-medium">
                  Image <span className="text-gray-400">(optional)</span>
                </label>

                {image || existingImage ? (
                  <div className="relative mt-3">
                    <img
                      src={image ? URL.createObjectURL(image) : existingImage}
                      className="w-full h-56 object-cover rounded"
                      alt="collection"
                    />

                    <div className="absolute top-2 right-2 flex gap-2">
                      <label className="bg-white shadow px-3 py-1 text-xs rounded cursor-pointer hover:bg-gray-100">
                        Change
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          onChange={handleImage}
                        />
                      </label>

                      <button
                        type="button"
                        onClick={() => {
                          setImage(null);
                          setExistingImage(null);
                        }}
                        className="bg-white shadow px-3 py-1 text-xs rounded text-red-600 hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="mt-3 flex flex-col items-center justify-center h-40 border-2 border-dashed rounded cursor-pointer hover:border-gray-400">
                    <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">
                      Upload collection image
                    </span>
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={handleImage}
                    />
                  </label>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* SAVE BAR */}
        <div className="bg-white border-t px-6 py-4 flex justify-end">
          <button
            onClick={saveCollection}
            disabled={submitLoading}
            className={`px-6 py-2.5 bg-[var(--primary-color)] text-white rounded-lg hover:bg-[var(--primary-hover)] text-sm font-medium
 ${submitLoading ? "cursor-not-allowed opacity-50" : "opacity-100"}`}
          >
            {submitLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </MasterLayout>
  );
}
